/**
 * Stubs for the now-removed Electron finder-tag bridge.
 *
 * The old app shipped a macOS-only Electron wrapper that spawned a local HTTP
 * helper on localhost:47821 to apply Finder color tags and resolve real
 * filesystem paths. The web-only build can't do either of those — browsers
 * have no Finder access — so this module exposes no-op stubs with the same
 * signatures so the call sites in PatientStep / GDriveFolderPicker still
 * compile and degrade gracefully.
 */

/** Always false in the web build. Kept so callers can branch on it. */
export function isElectronApp(): boolean {
  return false;
}

/** No-op in the browser. Resolves so callers can `await` without branching. */
export async function applyFinderTag(_folderPath: string, _color: string): Promise<void> {
  // No Finder access in the browser. Silently succeed.
}

/**
 * In Electron we could resolve a FileSystemDirectoryHandle to a real on-disk
 * path. In the browser we just hand back null and the caller's UI falls back
 * to the FSA-only path display.
 */
export async function pickFolderWithRealPath(): Promise<{
  handle: FileSystemDirectoryHandle;
  realPath: string | null;
} | null> {
  if (typeof window === "undefined") return null;
  const showDirectoryPicker = (window as Window & {
    showDirectoryPicker?: () => Promise<FileSystemDirectoryHandle>;
  }).showDirectoryPicker;
  if (typeof showDirectoryPicker !== "function") return null;
  try {
    const handle = await showDirectoryPicker.call(window);
    return { handle, realPath: null };
  } catch {
    return null;
  }
}
